/**
 * Shared SVG/PNG export engine for knowledge graph and knowledge map.
 * Replaces html2canvas-based export with pure SVG generation using ELK bend points.
 */

const EXPORT_PADDING = 40

/**
 * Escape XML special characters
 */
function escapeXml(str) {
  if (!str) return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/**
 * Measure text width using canvas (cached)
 */
function measureTextWidth(text, fontSize, fontWeight = 'normal') {
  if (!measureTextWidth._canvas) {
    measureTextWidth._canvas = document.createElement('canvas')
    measureTextWidth._ctx = measureTextWidth._canvas.getContext('2d')
  }
  const ctx = measureTextWidth._ctx
  if (!ctx) return text.length * fontSize * 0.6
  ctx.font = `${fontWeight} ${fontSize}px ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
  return ctx.measureText(text).width
}

/**
 * Wrap text into multiple lines for SVG
 */
function wrapText(text, maxWidth, fontSize, fontWeight = 'normal') {
  if (!text) return ['']
  const words = text.split(' ')
  const lines = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const testWidth = measureTextWidth(testLine, fontSize, fontWeight)

    if (testWidth <= maxWidth || !currentLine) {
      currentLine = testLine
    } else {
      lines.push(currentLine)
      currentLine = word
    }
  }

  if (currentLine) lines.push(currentLine)
  return lines.length ? lines : ['']
}

/**
 * Calculate graph bounding box from nodes and group nodes
 */
function calculateBounds(nodes, groupNodes = []) {
  const all = [...nodes, ...groupNodes]
  if (!all.length) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 }

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity

  for (const node of all) {
    const x = node.position.x
    const y = node.position.y
    const w = node.data?.width || node.style?.width || 220
    const h = node.data?.height || node.style?.height || 100
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x + w)
    maxY = Math.max(maxY, y + h)
  }

  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY }
}

/**
 * Build SVG path string from ELK bend points (orthogonal with rounded corners)
 */
function buildEdgePath(bendPoints) {
  if (!bendPoints || bendPoints.length < 2) return ''

  const radius = 6
  let d = `M ${bendPoints[0].x} ${bendPoints[0].y}`

  for (let i = 1; i < bendPoints.length - 1; i++) {
    const prev = bendPoints[i - 1]
    const curr = bendPoints[i]
    const next = bendPoints[i + 1]

    const dx1 = curr.x - prev.x
    const dy1 = curr.y - prev.y
    const dx2 = next.x - curr.x
    const dy2 = next.y - curr.y

    const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1)
    const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2)
    const r = Math.min(radius, len1 / 2, len2 / 2)

    if (r < 1 || len1 < 1 || len2 < 1) {
      d += ` L ${curr.x} ${curr.y}`
      continue
    }

    const beforeX = curr.x - (dx1 / len1) * r
    const beforeY = curr.y - (dy1 / len1) * r
    const afterX = curr.x + (dx2 / len2) * r
    const afterY = curr.y + (dy2 / len2) * r

    d += ` L ${beforeX} ${beforeY}`
    d += ` Q ${curr.x} ${curr.y} ${afterX} ${afterY}`
  }

  const last = bendPoints[bendPoints.length - 1]
  d += ` L ${last.x} ${last.y}`
  return d
}

/**
 * Generate SVG string for a knowledge graph/map export.
 *
 * @param {Array} nodes - Positioned flow nodes (type='knowledgeNode' or 'masteryNode')
 * @param {Array} edges - Routed edges with data.bendPoints
 * @param {Array} groupNodes - Domain/difficulty group nodes
 * @param {Object} options
 * @param {'admin'|'student'} options.mode - Export mode
 * @param {string} options.title - Export title
 * @param {string} options.subtitle - Export subtitle (e.g. student name + date)
 * @param {Array} options.strengths - Top strengths for student footer
 * @param {Array} options.focusAreas - Focus areas for student footer
 * @returns {string} Complete SVG string
 */
export function generateGraphSVG(nodes, edges, groupNodes = [], options = {}) {
  const {
    mode = 'admin',
    title = 'Knowledge Graph Export',
    subtitle = '',
    strengths = [],
    focusAreas = []
  } = options

  // Filter out group nodes from the main nodes array
  const contentNodes = nodes.filter((n) => n.type !== 'domainGroup')
  const groups = [...groupNodes, ...nodes.filter((n) => n.type === 'domainGroup')]

  const bounds = calculateBounds(contentNodes, groups)

  const headerHeight = subtitle ? 70 : 50
  const legendHeight = 60
  const footerHeight = strengths.length || focusAreas.length ? 80 : 0

  const svgWidth = bounds.width + EXPORT_PADDING * 2
  const svgHeight = bounds.height + EXPORT_PADDING * 2 + headerHeight + legendHeight + footerHeight

  const offsetX = bounds.minX - EXPORT_PADDING
  const offsetY = bounds.minY - EXPORT_PADDING

  // Build SVG content sections
  const headerSVG = buildHeaderSVG(title, subtitle, svgWidth, 0)
  const legendSVG = buildLegendSVG(svgWidth, headerHeight, mode)
  const groupsSVG = groups
    .map((g) => buildGroupSVG(g, offsetX, offsetY - headerHeight - legendHeight))
    .join('\n')
  const edgesSVG = edges
    .map((e) => buildEdgeSVGElement(e, offsetX, offsetY - headerHeight - legendHeight))
    .join('\n')
  const nodesSVG = contentNodes
    .map((n) => buildNodeSVG(n, offsetX, offsetY - headerHeight - legendHeight, mode))
    .join('\n')
  const footerSVG = footerHeight
    ? buildFooterSVG(strengths, focusAreas, svgWidth, svgHeight - footerHeight)
    : ''

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
  <defs>
    <marker id="arrowhead-prereq" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
      <polygon points="0 0, 10 3.5, 0 7" fill="#6366f1" />
    </marker>
    <marker id="arrowhead-student" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
      <polygon points="0 0, 10 3.5, 0 7" fill="#94A3B8" />
    </marker>
    <marker id="arrowhead-legend" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
      <polygon points="0 0, 6 2, 0 4" fill="#6366f1" />
    </marker>
  </defs>

  <rect width="100%" height="100%" fill="#ffffff" />

  ${headerSVG}
  ${legendSVG}

  <g transform="translate(0, ${headerHeight + legendHeight})">
    <g class="groups">${groupsSVG}</g>
    <g class="edges">${edgesSVG}</g>
    <g class="nodes">${nodesSVG}</g>
  </g>

  ${footerSVG}
</svg>`
}

function buildHeaderSVG(title, subtitle, width, y) {
  let svg = `
    <text x="${EXPORT_PADDING}" y="${y + 30}" font-size="20" font-weight="700" fill="#111827" font-family="ui-sans-serif, system-ui, sans-serif">
      ${escapeXml(title)}
    </text>`
  if (subtitle) {
    svg += `
    <text x="${EXPORT_PADDING}" y="${y + 52}" font-size="12" fill="#6b7280" font-family="ui-sans-serif, system-ui, sans-serif">
      ${escapeXml(subtitle)}
    </text>`
  }
  svg += `
    <text x="${width - EXPORT_PADDING}" y="${y + 30}" font-size="10" fill="#9ca3af" text-anchor="end" font-family="ui-sans-serif, system-ui, sans-serif">
      ${escapeXml(new Date().toLocaleDateString())}
    </text>`
  return svg
}

function buildLegendSVG(width, y, mode) {
  return `
    <g transform="translate(${EXPORT_PADDING}, ${y})">
      <rect x="0" y="0" width="${width - EXPORT_PADDING * 2}" height="50" rx="8" fill="#f9fafb" stroke="#e5e7eb" />

      <text x="16" y="20" font-size="11" font-weight="500" fill="#6b7280" font-family="ui-sans-serif, system-ui, sans-serif">Edges:</text>

      <line x1="70" y1="17" x2="95" y2="17" stroke="${mode === 'student' ? '#94A3B8' : '#6366f1'}" stroke-width="2" marker-end="url(#arrowhead-legend)" />
      <text x="102" y="20" font-size="11" fill="#374151" font-family="ui-sans-serif, system-ui, sans-serif">Prerequisite</text>

      ${
        mode === 'admin'
          ? `
      <line x1="180" y1="17" x2="205" y2="17" stroke="#9ca3af" stroke-width="1" stroke-dasharray="4,3" />
      <text x="212" y="20" font-size="11" fill="#374151" font-family="ui-sans-serif, system-ui, sans-serif">Related</text>
      `
          : ''
      }

      <line x1="${mode === 'admin' ? 275 : 200}" y1="8" x2="${mode === 'admin' ? 275 : 200}" y2="26" stroke="#d1d5db" />

      <text x="${mode === 'admin' ? 290 : 215}" y="20" font-size="11" font-weight="500" fill="#6b7280" font-family="ui-sans-serif, system-ui, sans-serif">Difficulty:</text>

      <rect x="${mode === 'admin' ? 360 : 285}" y="10" width="36" height="14" rx="3" fill="#dcfce7" />
      <text x="${mode === 'admin' ? 378 : 303}" y="20" font-size="9" font-weight="500" fill="#15803d" text-anchor="middle" font-family="ui-sans-serif, system-ui, sans-serif">Easy</text>

      <rect x="${mode === 'admin' ? 405 : 330}" y="10" width="48" height="14" rx="3" fill="#fef9c3" />
      <text x="${mode === 'admin' ? 429 : 354}" y="20" font-size="9" font-weight="500" fill="#a16207" text-anchor="middle" font-family="ui-sans-serif, system-ui, sans-serif">Medium</text>

      <rect x="${mode === 'admin' ? 462 : 387}" y="10" width="36" height="14" rx="3" fill="#fee2e2" />
      <text x="${mode === 'admin' ? 480 : 405}" y="20" font-size="9" font-weight="500" fill="#dc2626" text-anchor="middle" font-family="ui-sans-serif, system-ui, sans-serif">Hard</text>

      <text x="${width - EXPORT_PADDING * 2 - 16}" y="42" font-size="10" fill="#9ca3af" text-anchor="end" font-family="ui-sans-serif, system-ui, sans-serif">
        Knowledge Graph Export
      </text>
    </g>`
}

function buildGroupSVG(groupNode, offsetX, offsetY) {
  const x = groupNode.position.x - offsetX
  const y = groupNode.position.y - offsetY
  const w = groupNode.data?.width || groupNode.style?.width || 300
  const h = groupNode.data?.height || groupNode.style?.height || 200
  const label = groupNode.data?.label || ''
  const testType = groupNode.data?.test_type

  const isMath = testType === 'math'
  const borderColor = isMath ? '#93c5fd' : '#c4b5fd'
  const bgColor = isMath ? 'rgba(219, 234, 254, 0.15)' : 'rgba(237, 233, 254, 0.15)'
  const textColor = isMath ? '#2563eb' : '#7c3aed'

  return `
    <g transform="translate(${x}, ${y})">
      <rect width="${w}" height="${h}" rx="12" fill="${bgColor}" stroke="${borderColor}" stroke-width="1.5" stroke-dasharray="6,4" />
      <text x="14" y="22" font-size="12" font-weight="600" fill="${textColor}" font-family="ui-sans-serif, system-ui, sans-serif">
        ${escapeXml(label)}
      </text>
    </g>`
}

function buildEdgeSVGElement(edge, offsetX, offsetY) {
  const bendPoints = edge.data?.bendPoints || []
  if (!bendPoints.length) return ''

  const isPrereq = edge.data?.isPrerequisite !== false
  const strokeColor = edge.style?.stroke || (isPrereq ? '#6366f1' : '#9ca3af')
  const strokeWidth = edge.style?.strokeWidth || (isPrereq ? 2 : 1)
  const dashArray = !isPrereq ? 'stroke-dasharray="5,5"' : ''
  const opacity = edge.style?.opacity ?? 1
  const markerEnd = isPrereq ? 'marker-end="url(#arrowhead-prereq)"' : ''

  // Offset bend points
  const offsetPoints = bendPoints.map((p) => ({
    x: p.x - offsetX,
    y: p.y - offsetY
  }))

  const path = buildEdgePath(offsetPoints)
  if (!path) return ''

  return `
    <path
      d="${path}"
      fill="none"
      stroke="${strokeColor}"
      stroke-width="${strokeWidth}"
      stroke-linecap="round"
      stroke-linejoin="round"
      ${dashArray}
      opacity="${opacity}"
      ${markerEnd}
    />`
}

function buildNodeSVG(node, offsetX, offsetY, mode) {
  if (mode === 'student') {
    return buildStudentNodeSVG(node, offsetX, offsetY)
  }
  return buildAdminNodeSVG(node, offsetX, offsetY)
}

function buildAdminNodeSVG(node, offsetX, offsetY) {
  const x = node.position.x - offsetX
  const y = node.position.y - offsetY
  const data = node.data
  const nodeWidth = 220
  const nodeHeight = data.height || 100

  const testTypeColors = {
    math: { bg: '#eff6ff', border: '#3b82f6', text: '#2563eb' },
    reading_writing: { bg: '#faf5ff', border: '#a855f7', text: '#9333ea' }
  }
  const difficultyColors = {
    E: { bg: '#dcfce7', text: '#15803d', label: 'Easy' },
    M: { bg: '#fef9c3', text: '#a16207', label: 'Medium' },
    H: { bg: '#fee2e2', text: '#dc2626', label: 'Hard' }
  }

  const tc = testTypeColors[data.test_type] || testTypeColors.math
  const dc = difficultyColors[data.difficulty] || difficultyColors.M

  const textMaxWidth = nodeWidth - 24
  const skillLines = wrapText(data.skill || '', textMaxWidth, 13, '600')
  const skillLineHeight = 16
  const skillTspans = skillLines
    .map(
      (line, i) =>
        `<tspan x="12" ${i === 0 ? '' : `dy="${skillLineHeight}"`}>${escapeXml(line)}</tspan>`
    )
    .join('')
  const skillTextHeight = skillLines.length * skillLineHeight
  const badgesY = 18 + 8 + skillTextHeight + 8

  return `
    <g transform="translate(${x}, ${y})">
      <rect width="${nodeWidth}" height="${nodeHeight}" rx="8" fill="${tc.bg}" stroke="${tc.border}" stroke-width="2" />
      <text x="12" y="18" font-size="10" font-weight="500" fill="${tc.text}" font-family="ui-sans-serif, system-ui, sans-serif">
        ${escapeXml(data.domain || '')}
      </text>
      <text x="12" y="36" font-size="13" font-weight="600" fill="#111827" font-family="ui-sans-serif, system-ui, sans-serif">
        ${skillTspans}
      </text>
      <rect x="12" y="${badgesY}" width="42" height="18" rx="4" fill="${dc.bg}" />
      <text x="33" y="${badgesY + 13}" font-size="10" font-weight="500" fill="${dc.text}" text-anchor="middle" font-family="ui-sans-serif, system-ui, sans-serif">
        ${dc.label}
      </text>
      <text x="120" y="${badgesY + 13}" font-size="10" fill="#6b7280" text-anchor="middle" font-family="ui-sans-serif, system-ui, sans-serif">
        DOK ${escapeXml(data.dok_level || '')}
      </text>
      <text x="190" y="${badgesY + 13}" font-size="10" font-weight="500" fill="#374151" text-anchor="end" font-family="ui-sans-serif, system-ui, sans-serif">
        ${escapeXml(String(data.question_count || 0))} Q
      </text>
    </g>`
}

function buildStudentNodeSVG(node, offsetX, offsetY) {
  const x = node.position.x - offsetX
  const y = node.position.y - offsetY
  const data = node.data
  const nodeWidth = 240
  const nodeHeight = 80

  const STATUS_COLORS = {
    UNVISITED: { bg: 'rgba(156,163,175,0.1)', border: '#9CA3AF', dot: '#9CA3AF' },
    STRUGGLING: { bg: 'rgba(239,68,68,0.1)', border: '#EF4444', dot: '#EF4444' },
    IN_PROGRESS: { bg: 'rgba(234,179,8,0.1)', border: '#EAB308', dot: '#EAB308' },
    MASTERED: { bg: 'rgba(34,197,94,0.1)', border: '#22C55E', dot: '#22C55E' }
  }
  const DIFFICULTY_BADGE = {
    E: { bg: '#DCFCE7', text: '#166534' },
    M: { bg: '#FEF3C7', text: '#92400E' },
    H: { bg: '#FEE2E2', text: '#991B1B' }
  }

  const mastery = data.mastery || { status: 'UNVISITED', totalAttempts: 0, correctAttempts: 0 }
  const colors = STATUS_COLORS[mastery.status] || STATUS_COLORS.UNVISITED
  const badge = DIFFICULTY_BADGE[data.difficulty] || DIFFICULTY_BADGE.M
  const pct =
    mastery.totalAttempts > 0
      ? Math.round((mastery.correctAttempts / mastery.totalAttempts) * 100)
      : 0

  const progressBar =
    mastery.totalAttempts > 0
      ? `<rect x="12" y="62" width="216" height="5" rx="3" fill="#e5e7eb" />
       <rect x="12" y="62" width="${Math.round((216 * pct) / 100)}" height="5" rx="3" fill="${colors.border}" />`
      : ''

  const statsText =
    mastery.totalAttempts > 0
      ? `<text x="228" y="48" font-size="11" fill="#6b7280" text-anchor="end" font-family="ui-sans-serif, system-ui, sans-serif">${mastery.correctAttempts}/${mastery.totalAttempts}</text>`
      : ''

  return `
    <g transform="translate(${x}, ${y})">
      <rect width="${nodeWidth}" height="${nodeHeight}" rx="8" fill="${colors.bg}" stroke="${colors.border}" stroke-width="2" />
      <circle cx="22" cy="20" r="5" fill="${colors.dot}" />
      <text x="34" y="24" font-size="13" font-weight="500" fill="#1f2937" font-family="ui-sans-serif, system-ui, sans-serif">
        ${escapeXml((data.skill || '').length > 24 ? data.skill.slice(0, 24) + '...' : data.skill || '')}
      </text>
      <rect x="200" y="11" width="28" height="16" rx="4" fill="${badge.bg}" />
      <text x="214" y="23" font-size="10" font-weight="700" fill="${badge.text}" text-anchor="middle" font-family="ui-sans-serif, system-ui, sans-serif">
        ${escapeXml(data.difficulty)}
      </text>
      <text x="12" y="48" font-size="11" fill="#6b7280" font-family="ui-sans-serif, system-ui, sans-serif">${escapeXml(data.domain)}</text>
      ${statsText}
      ${progressBar}
    </g>`
}

function buildFooterSVG(strengths, focusAreas, width, y) {
  let svg = `
    <rect x="0" y="${y}" width="${width}" height="80" fill="rgba(255,255,255,0.95)" stroke="#e5e7eb" stroke-width="1" />`

  if (strengths.length) {
    svg += `
    <text x="${EXPORT_PADDING}" y="${y + 22}" font-size="12" font-weight="700" fill="#22c55e" font-family="ui-sans-serif, system-ui, sans-serif">Top Strengths:</text>`
    strengths.forEach((s, i) => {
      svg += `
    <text x="${EXPORT_PADDING}" y="${y + 38 + i * 14}" font-size="11" fill="#374151" font-family="ui-sans-serif, system-ui, sans-serif">${i + 1}. ${escapeXml(s)}</text>`
    })
  }

  if (focusAreas.length) {
    const midX = width / 2
    svg += `
    <text x="${midX}" y="${y + 22}" font-size="12" font-weight="700" fill="#ef4444" font-family="ui-sans-serif, system-ui, sans-serif">Focus Areas:</text>`
    focusAreas.forEach((s, i) => {
      svg += `
    <text x="${midX}" y="${y + 38 + i * 14}" font-size="11" fill="#374151" font-family="ui-sans-serif, system-ui, sans-serif">${i + 1}. ${escapeXml(s)}</text>`
    })
  }

  return svg
}

/**
 * Export SVG string as PNG via canvas rendering.
 * @param {string} svgString - Complete SVG markup
 * @param {number} scale - DPI scale factor (default 2)
 * @param {string} filename - Download filename
 */
export function exportAsPNG(svgString, scale = 2, filename = 'graph-export.png') {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width * scale
      canvas.height = img.height * scale
      const ctx = canvas.getContext('2d')
      ctx.scale(scale, scale)
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create PNG blob'))
            return
          }
          const pngUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = pngUrl
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(pngUrl)
          resolve(filename)
        },
        'image/png',
        1.0
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load SVG for PNG conversion'))
    }

    img.src = url
  })
}

/**
 * Export SVG string as an SVG file download.
 * @param {string} svgString - Complete SVG markup
 * @param {string} filename - Download filename
 */
export function exportAsSVG(svgString, filename = 'graph-export.svg') {
  const blob = new Blob([svgString], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
  return filename
}

/**
 * Generate descriptive filename for export.
 */
export function generateExportFilename(format, layoutMode, filters) {
  const parts = ['knowledge-graph']
  parts.push(layoutMode)

  if (filters.testType && filters.testType !== 'all') {
    parts.push(filters.testType)
  }
  if (filters.domain && filters.domain !== 'all') {
    parts.push(filters.domain.toLowerCase().replace(/[^a-z0-9]+/g, '-'))
  }
  if (filters.difficulty && filters.difficulty !== 'all') {
    parts.push(`diff-${filters.difficulty}`)
  }
  if (filters.dokLevel && filters.dokLevel !== 'all') {
    parts.push(`dok-${filters.dokLevel}`)
  }

  const timestamp = new Date().toISOString().slice(0, 10)
  parts.push(timestamp)
  return `${parts.join('-')}.${format}`
}
